import { accessLevel, can } from '@hrms/shared'
import { NAV_EVENTS } from '../shell/navEvents.ts'
import { registerTour, type TourStep, tourTarget } from './registry.ts'

registerTour({
  id: 'shell-intro',
  title: 'Find your way around',
  summary: 'The menu, themes and where to get help',
  autoStart: true,
  order: 0,
  steps: (user) => {
    const level = accessLevel(user)
    const narrow = window.matchMedia('(max-width: 767.98px)').matches
    const steps: TourStep[] = [
      {
        popover: {
          title: 'Welcome to HR desk',
          description:
            level === 'employee'
              ? 'This is where you ask HR for help and follow up on your requests. A quick look around takes under a minute.'
              : level === 'viewer'
                ? 'You have view-only access: you can look through HR tickets but not change them. Here’s a quick look around.'
                : 'This is where the HR team triages, answers and resolves employee requests. Here’s a quick look around.',
        },
      },
      {
        element: tourTarget('module-nav'),
        popover: {
          title: 'Pages',
          description:
            level === 'employee'
              ? 'My tickets lists everything you’ve raised. More HR tools will appear here over time.'
              : can(user, 'settings.view')
                ? 'Tickets is the full queue, Board shows it by status, and Settings holds categories, priorities, statuses and people.'
                : 'Tickets is the full queue and Board shows the same tickets as columns by status.',
          side: narrow ? 'bottom' : 'right',
          align: 'start',
        },
        // On small screens the nav is an off-canvas drawer: slide it in before highlighting.
        prepare: async () => {
          if (!window.matchMedia('(max-width: 767.98px)').matches) return
          window.dispatchEvent(new Event(NAV_EVENTS.open))
          await new Promise((r) => window.setTimeout(r, 260))
        },
        onDeselected: () => window.dispatchEvent(new Event(NAV_EVENTS.close)),
      },
      {
        element: tourTarget('hamburger'),
        popover: {
          title: 'Menu',
          description: 'Show or hide the side menu. On a phone it slides in from the left.',
          side: 'bottom',
          align: 'start',
        },
      },
    ]
    if (can(user, 'ticket.create.self'))
      steps.push({
        element: tourTarget('raise-ticket'),
        popover: {
          title: level === 'employee' ? 'Raise a ticket' : 'Log a ticket',
          description:
            level === 'employee'
              ? 'Leave, payroll, reimbursements, the office: ask HR anything from here, on any page.'
              : 'Log a ticket for someone who came to you in person or over email.',
          side: 'bottom',
          align: 'end',
        },
      })
    if (can(user, 'queue.view'))
      steps.push({
        element: tourTarget('live-dot'),
        popover: {
          title: 'Live updates',
          description:
            'Green means changes from your colleagues show up within seconds, no refresh needed.',
          side: 'bottom',
          align: 'end',
        },
      })
    steps.push(
      {
        element: tourTarget('theme-toggle'),
        popover: {
          title: 'Light or dark',
          description: 'Switch between system, light and dark themes. Your choice is remembered.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: tourTarget('user-menu'),
        popover: {
          title: 'Tours live here',
          description: 'Open your menu and choose “Take a tour” to replay this or a page tour.',
          side: 'bottom',
          align: 'end',
        },
      },
    )
    return steps
  },
})
